import { useEffect, useRef, useState, useCallback } from "react";

/**
 * iOS 스타일 바텀시트
 * mode: "peek" | "half" | "full"
 *
 * 드래그 동작 (iOS Maps 패턴):
 * - drag handle 영역: 항상 시트 드래그
 * - body 영역: scrollTop === 0 일 때만 시트 드래그, 아니면 콘텐츠 스크롤
 *   - peek/half + 위로: 시트 올림 (scrollTop 0이라 가정)
 *   - full + scrollTop 0 + 아래로: 시트 내림
 *   - full + scrollTop > 0: 콘텐츠 스크롤
 * - 시트 드래그 중에는 body의 scrollTop 을 고정해서 콘텐츠 스크롤 막음
 *
 * ★ 퍼포먼스: 드래그 중에는 setState 안 함. ref로 DOM 직접 조작.
 *   드래그 끝나는 순간만 부모 onModeChange로 한 번 알림 → 리렌더 1회.
 */
const SHEET_FULL_TOP_PX = 0; // full 모드 시 화면 끝까지
const SNAP_TO_FULL_THRESHOLD = 80; // 이 px 이하로 드래그 됨어올리면 자동 full 로 스납
const TRANSITION_SNAP = "transform 0.42s cubic-bezier(0.32, 0.72, 0, 1)";

function getSnapOffsetPx(snap, vh) {
  if (snap === "peek") return vh - 160;
  if (snap === "half") return vh * 0.5;
  if (snap === "full") return SHEET_FULL_TOP_PX;
  if (snap === "closed") return vh;
  return vh - 160;
}

function findClosestSnap(currentOffset, vh, velocity) {
  const FLICK_THRESHOLD = 0.6;

  if (Math.abs(velocity) > FLICK_THRESHOLD) {
    if (velocity > 0) {
      if (currentOffset < vh * 0.3) return "half";
      if (currentOffset < vh * 0.65) return "peek";
      return "closed";
    } else {
      if (currentOffset > vh * 0.7) return "half";
      return "full";
    }
  }

  const offsets = {
    peek: getSnapOffsetPx("peek", vh),
    half: getSnapOffsetPx("half", vh),
    full: getSnapOffsetPx("full", vh),
  };
  let closest = "peek";
  let minDiff = Infinity;
  for (const [snap, off] of Object.entries(offsets)) {
    const diff = Math.abs(currentOffset - off);
    if (diff < minDiff) {
      minDiff = diff;
      closest = snap;
    }
  }
  return closest;
}

export default function Sheet({
  open = true,
  mode = "peek",
  onModeChange,
  onDismiss,
  dismissible = false,
  header,
  children,
}) {
  const [vh, setVh] = useState(
    typeof window !== "undefined" ? window.innerHeight : 800
  );

  // ── refs (drag 중 DOM 직접 조작용) ─────────────────────
  const sheetRef = useRef(null);
  const bodyRef = useRef(null);
  const offsetRef = useRef(getSnapOffsetPx(mode, vh));

  const startY = useRef(0);
  const startOffset = useRef(0);
  const startScrollTop = useRef(0);
  const lastY = useRef(0);
  const lastTime = useRef(0);
  const velocity = useRef(0);

  // 'sheet' | 'scroll' | null — 한 번의 터치 제스처 안에서 어떤 모드인지 결정
  const dragModeRef = useRef(null);
  const isDraggingRef = useRef(false);

  // viewport 변경 감지 (resize 시에만 state 업데이트 — 자주 안 일어남)
  useEffect(() => {
    const onResize = () => setVh(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // mode / vh / open 변경 시 → snap 위치로 transition (drag 중 아닐 때만)
  useEffect(() => {
    if (isDraggingRef.current) return;
    const target = open ? getSnapOffsetPx(mode, vh) : vh;
    offsetRef.current = target;
    if (sheetRef.current) {
      sheetRef.current.style.transition = TRANSITION_SNAP;
      sheetRef.current.style.transform = `translateY(${target}px)`;
    }
  }, [mode, vh, open]);

  // ── 통합 touch 핸들러 ────────────────────────────────────
  // drag-area 와 body 양쪽에서 같이 사용. fromHandle=true 이면
  // scrollTop 무시하고 항상 시트 드래그.
  const onTouchStart = useCallback((e) => {
    startY.current = e.touches[0].clientY;
    startOffset.current = offsetRef.current;
    startScrollTop.current = bodyRef.current?.scrollTop ?? 0;
    lastY.current = e.touches[0].clientY;
    lastTime.current = Date.now();
    velocity.current = 0;
    dragModeRef.current = null;
  }, []);

  const onTouchMove = useCallback(
    (e, fromHandle = false) => {
      const currentY = e.touches[0].clientY;
      const dy = currentY - startY.current;

      // 드래그 모드 결정 (한 제스처 안에서 한 번만)
      if (dragModeRef.current === null) {
        const movingUp = dy < -4;
        const movingDown = dy > 4;
        if (!movingUp && !movingDown) return; // 임계값 이하면 보류

        const scrollTop = bodyRef.current?.scrollTop ?? 0;

        if (fromHandle) {
          dragModeRef.current = "sheet";
          isDraggingRef.current = true;
          // drag 시작 — transition off (ref 직접 조작)
          if (sheetRef.current) sheetRef.current.style.transition = "none";
        } else if (movingUp) {
          if (mode !== "full") {
            dragModeRef.current = "sheet";
            isDraggingRef.current = true;
            if (sheetRef.current) sheetRef.current.style.transition = "none";
          } else {
            dragModeRef.current = "scroll";
          }
        } else if (movingDown) {
          if (scrollTop === 0) {
            dragModeRef.current = "sheet";
            isDraggingRef.current = true;
            if (sheetRef.current) sheetRef.current.style.transition = "none";
          } else {
            dragModeRef.current = "scroll";
          }
        }
      }

      // 시트 드래그 — ref로 직접 transform 변경 (setState 안 함)
      if (dragModeRef.current === "sheet") {
        if (bodyRef.current) {
          bodyRef.current.scrollTop = startScrollTop.current;
        }
        const newOffset = Math.max(0, startOffset.current + dy);

        // 칩 영역 근처에 도달하면 자동 full 스납
        if (newOffset < SNAP_TO_FULL_THRESHOLD && mode !== "full") {
          isDraggingRef.current = false;
          dragModeRef.current = "scroll";
          offsetRef.current = SHEET_FULL_TOP_PX;
          if (sheetRef.current) {
            sheetRef.current.style.transition = TRANSITION_SNAP;
            sheetRef.current.style.transform = `translateY(${SHEET_FULL_TOP_PX}px)`;
          }
          onModeChange?.("full");
          return;
        }

        offsetRef.current = newOffset;
        if (sheetRef.current) {
          sheetRef.current.style.transform = `translateY(${newOffset}px)`;
        }

        const now = Date.now();
        const dt = now - lastTime.current;
        if (dt > 0) {
          velocity.current = (currentY - lastY.current) / dt;
        }
        lastY.current = currentY;
        lastTime.current = now;
      }
    },
    [mode, onModeChange]
  );

  const onTouchEnd = useCallback(() => {
    if (dragModeRef.current === "sheet") {
      isDraggingRef.current = false;
      const next = findClosestSnap(offsetRef.current, vh, velocity.current);

      if (next === "closed") {
        if (dismissible) {
          onDismiss?.();
        } else {
          // peek 로 스납
          const target = getSnapOffsetPx("peek", vh);
          offsetRef.current = target;
          if (sheetRef.current) {
            sheetRef.current.style.transition = TRANSITION_SNAP;
            sheetRef.current.style.transform = `translateY(${target}px)`;
          }
          onModeChange?.("peek");
        }
      } else {
        const target = getSnapOffsetPx(next, vh);
        offsetRef.current = target;
        if (sheetRef.current) {
          sheetRef.current.style.transition = TRANSITION_SNAP;
          sheetRef.current.style.transform = `translateY(${target}px)`;
        }
        onModeChange?.(next);
      }
    }
    dragModeRef.current = null;
  }, [vh, dismissible, onDismiss, onModeChange]);

  // 초기 transform — useEffect로 mount 시 한 번
  const initialOffset = getSnapOffsetPx(mode, vh);

  return (
    <div
      ref={sheetRef}
      className={`cf-sheet cf-sheet--${mode}`}
      style={{
        transform: `translateY(${initialOffset}px)`,
        transition: TRANSITION_SNAP,
      }}
    >
      {/* drag handle 영역 — 항상 시트 드래그 */}
      <div
        className="cf-sheet__drag-area"
        onTouchStart={onTouchStart}
        onTouchMove={(e) => onTouchMove(e, true)}
        onTouchEnd={onTouchEnd}
      >
        <div className="cf-sheet__handle">
          <span />
        </div>
      </div>

      {/* header slot */}
      {header}

      {/* body — touch 이벤트도 받음 (scrollTop 기반 분기) */}
      <div
        ref={bodyRef}
        className="cf-sheet__body"
        onTouchStart={onTouchStart}
        onTouchMove={(e) => onTouchMove(e, false)}
        onTouchEnd={onTouchEnd}
      >
        {children}
      </div>
    </div>
  );
}
